import { KDV_VARSAYILAN, YAT_SAAT, yeniOdaKimligi } from './mantik';
import type { EkranEki } from './taslak-depo';
import type { AracSecimi, MesajTonu, OdaTaslagi, PaketKodu, SerbestKalem, TaslakBolum, TeklifTaslagi, YatBoyutu } from './tipler';
import type { PaketDuzenlemeTaslaklari } from './paket-duzenleme';

export interface GeriKurulanBolum {
  readonly anahtar: string;
  readonly giris: string;
  readonly cikis: string;
}

/** Kayıtlı taslaktan ekranın React durumuna geri kurulan değerler (salt okunur sürüm görüntülemeden ayrı). */
export interface GeriKurulanDurum {
  readonly odalar: OdaTaslagi[];
  readonly bolumler: readonly GeriKurulanBolum[];
  /** Bölüm anahtarı -> o bölümün kaydedilmiş durumu (oteller, fiyat sonuçları, seçimler, satış fiyatları). */
  readonly bolumVeri: Readonly<Record<string, TaslakBolum>>;
  readonly aktifAnahtar: string;
  readonly bolumSayaci: number;
  readonly uyruk: string;
  readonly paketAcik: readonly PaketKodu[];
  readonly paketNotlari: Readonly<Partial<Record<PaketKodu, string>>>;
  readonly teklifNotu: string;
  readonly aracSecimi: AracSecimi | null;
  readonly kalemler: readonly SerbestKalem[];
  readonly kdvNotu: string;
  readonly planlamaMetni: string;
  readonly isleyisMetni: string;
  readonly safariKoltuk: number;
  readonly yatSaat: string;
  readonly yatBoyut: YatBoyutu;
  readonly paketDuzenleme: { readonly imza: string; readonly degerler: PaketDuzenlemeTaslaklari } | null;
  readonly destinasyon: string | null;
  readonly pdfDili: 'tr' | 'en';
  readonly ton: MesajTonu;
}

/**
 * Taslağı düzenlenebilir ekran durumuna çevirir. Eksik alanlar (eski taslak) varsayılana düşer: bölüm yoksa tek boş bölüm,
 * KDV notu varsayılan metin, yat 2 saat / 50 ft, dil tr, ton SICAK. Oda kimlikleri yeniden üretilir (`kimlikUret`).
 */
export function taslagiGeriKur(taslak: TeklifTaslagi, musteriId: string, ekran: EkranEki | undefined, kimlikUret: () => string = yeniOdaKimligi): GeriKurulanDurum {
  // Eski ya da bozuk kayıtlarda alanlar eksik olabilir: okuma kısmi şekil üzerinden yapılır.
  const t: Partial<TeklifTaslagi> = taslak;
  const odalar: OdaTaslagi[] = (t.talep?.odalar ?? []).map((o) => ({
    id: kimlikUret(),
    yetiskin: o.yetiskin,
    cocukYaslari: o.cocukYaslari.map(String),
    ...(o.cocukAylari === undefined ? {} : { cocukAylari: o.cocukAylari.map((a) => (a > 0 ? String(a) : '')) }),
  }));
  const kayitliBolumler = t.bolumler ?? [];
  const bolumler: GeriKurulanBolum[] = kayitliBolumler.length === 0
    ? [{ anahtar: 'b1', giris: '', cikis: '' }]
    : kayitliBolumler.map((b) => ({ anahtar: b.anahtar, giris: b.girisTarihi, cikis: b.cikisTarihi }));
  const bolumVeri: Record<string, TaslakBolum> = {};
  for (const b of kayitliBolumler) bolumVeri[b.anahtar] = b;
  const sayilar = bolumler.map((b) => /^b(\d+)$/.exec(b.anahtar)).map((e) => (e === null ? 0 : Number(e[1])));
  const uyruk = t.talep?.uyruk ?? 'TR';
  // Paket düzenlemesi ekranın talep imzasına bağlıdır (ekrandaki imza ile aynı biçim).
  const talepAnahtari = JSON.stringify([bolumler.map((b) => [b.giris, b.cikis]), odalar.map((o) => [o.yetiskin, o.cocukYaslari, o.cocukAylari ?? []]), uyruk]);
  const duzenleme = taslak.paketDuzenleme;
  return {
    odalar: odalar.length === 0 ? [{ id: kimlikUret(), yetiskin: 2, cocukYaslari: [] }] : odalar,
    bolumler,
    bolumVeri,
    aktifAnahtar: bolumler[0]?.anahtar ?? 'b1',
    bolumSayaci: Math.max(bolumler.length, ...sayilar),
    uyruk,
    paketAcik: t.paketler ?? [],
    paketNotlari: taslak.paketNotlari ?? {},
    teklifNotu: taslak.teklifNotu ?? '',
    aracSecimi: taslak.aracSecimi ?? null,
    kalemler: taslak.serbestKalemler ?? [],
    kdvNotu: taslak.kdvNotu ?? KDV_VARSAYILAN,
    planlamaMetni: taslak.planlamaMetni ?? '',
    isleyisMetni: taslak.isleyisMetni ?? '',
    safariKoltuk: taslak.safariBebekKoltukSayisi ?? 0,
    yatSaat: String(taslak.yat?.saat ?? YAT_SAAT.varsayilan),
    yatBoyut: taslak.yat?.boyut ?? 'YAT_50FT',
    paketDuzenleme: duzenleme === undefined ? null : { imza: JSON.stringify([musteriId, talepAnahtari]), degerler: duzenleme.degerler },
    destinasyon: ekran?.destinasyon ?? null,
    pdfDili: ekran?.pdfDili ?? 'tr',
    ton: ekran?.ton ?? 'SICAK',
  };
}
